<template lang="pug">
    span(
        class="_field",
        :class="_class",
        :style="{ width, height }",
        v-on="__fwd")
        div(v-if="$slots.start", class="_flds")
            slot(name="start")
        span(class="_fldi")
            | {{ _prefix }}
            label(
                v-if="privacy || _placeholder",
                v-text="security || _placeholder",
                :for="_id",
                class="_fldl",
                :class="{ _dock: !security && !empty }")
            component(
                class="_fldin",
                :class="_iclass",
                :is="multiline ? 'textarea' : 'input'",
                :id="_id",
                :name="name",
                :required="required",
                :readonly="readonly",
                :disabled="disabled",
                :inert="inert",
                :autofocus="autofocus",
                :tabindex="inert ? -1 : tabindex",
                @change="change",
                @drop="drop",
                @paste="paste",
                @keydown="keydown",
                @keydown.enter="onEnter",
                @keypress="keypress",
                @input="input",
                @select="_select",
                v-on="__on",
                v-bind="bind",
                ref="_in")
            | {{ _suffix }}
        div(v-if="$slots.default || $slots.end", class="_flde")
            slot
            slot(name="end")
</template>

<script>
    import $device from '../../prototype/device';
    import entry from './ib-form-entry.vue';
    import telemetry from '../private/telemetry';

    const bullet = '•';
    const zwnj = '\u200c';    // zero width non joiner; enforces baseline alignment in absence of other text


    /**
     * A standard input field that supports validation, v-model, and common styling.
     */
    export default {
        name: 'ib-field',

        mixins: [
            entry,
            telemetry
        ],

        props: {
            /**
             * Value of field; typically implemented using `v-model`.
             */
            value: {
                type: [String,Number],
                default: undefined
            },
            /**
             * HTML input field type.
             */
            type: {
                type: String,
                default: 'text'
            },
            /**
             * Expected writing system; by default, device setting; 'latn' is supported. This can
             * help ensure the cursor appears on the right in Right to Left languages when entering
             * things like usernames or ticker symbols.
             */
            script: String,
            /**
             * If true, allow multiline input. Typically used with the `height` property.
             */
            multiline: Boolean,
            /**
             * Appearance modifier: `box`, `rounded`, `pill`, `frameless` or `underline`. The
             * default is `underline` on Android, `rounded` on iOS, and `box` on other platforms.
             */
            appearance: String,
            /**
             * A prefix that is always displayed in the input box, but is not editable and is not
             * emitted as value. If present, the label placeholder will not be displayed.
             */
            prefix: String,
            /**
             * A placeholder string displayed when field content is empty.
             */
            placeholder: String,
            /**
             * If true, convert all input to uppercase.
             */
            uppercase: Boolean,
            /**
             * If true, convert all input to lowercase.
             */
            lowercase: Boolean,
            /**
             * If true, replace displayed content with bullet placeholders on blur.
             */
            privacy: Boolean,
            /**
             * A regexp pattern that the field input should match before being marked as valid.
             */
            pattern: String,
            /**
             * Maximum characters allowed in the field
             */
            maxlength: Number,
            /**
             * Field should take focus when made visible.
             */
            autofocus: Boolean,
            /**
             * Field contents should be selected automatically on focus.
             */
            autoselect: Boolean,
            /**
             * Specify what permission user agent has to provide autofill.
             */
            autocomplete: String,
            /**
             * Specify whether and how text input is automatically capitalized on mobile devices.
             */
            autocapitalize: String,
            /**
             * [Safari Extension] Indicates whether or not to activate automatic correction.
             */
            autocorrect: String
        },

        data() {
            return {
                dselect: false,     // Desktop autoselected state; prevents next mouseup event
                mselect: null,      // Mobile autoselected state; simulates highlight
                security: null,     // Privacy mode placeholder
                empty: true,
                numeric: false
            }
        },

        computed: {
            bind() {
                return {
                    type:
                        this.multiline ? null : this.type,
                    maxlength:
                        this.maxlength,
                    pattern:
                        this.pattern || (this.required ? '.*\\S+.*' : null),
                    autocomplete:
                        this.autocomplete,
                    autocapitalize:
                        this.uppercase ? 'character' :
                        this.lowercase ? 'none' :
                        this.autocapitalize,
                    autocorrect:
                        this.autocorrect,
                    'class':
                        this.uppercase ? 'uppercase' :
                        this.lowercase ? 'lowercase' : null
                };
            },

            _id() {
                return this.id || '_f' + this._uid;
            },

            _class() {
                return {
                    _fo: this.focused,
                    _ml: this.multiline,
                    _inv: this.showvalid && !this.valid,
                    _sec: this.privacy && !this.empty && !this.focused,
                    _rtl: this.$rtl,
                    ['_a'+this.align]: this.align,
                    ['_e'+this.appearance]: this.appearance,
                    ['_s'+this.script]: this.script
                }
            },

            _iclass() {
                // undefined
            },

            _placeholder() {
                return this.placeholder;
            },

            _prefix() {
                return this.prefix != null && this.prefix != '' ? this.prefix : zwnj;
            },

            _suffix() {
                return null;
            },

            /** Convert stored value to displayed value */
            format() {
                return this.privacy
                    ? value => value == null ? '' : this.focused ? value : value.replace(/./g, bullet)
                    : value => value == null ? '' : value;
            }
        },

        watch: {
            multiline() {
                this.$nextTick(this._sync);
            },
            pattern() {
                this.validate();
            },
            privacy(privacy) {
                this.security = (privacy && !this.focused)
                    ? this.$refs._in.value.replace(/./g, bullet)
                    : null;
            }
        },

        mounted() {
            if (this.autofocus && $device.hasKeyboard) {
                this.$refs._in.focus();
            }
            if (this.privacy && !this.focused) {
                this.security = this.$refs._in.value.replace(/./g, bullet);
            }
        },

        methods: {
            /** Set selection range */
            select(start = 0, end = 2E9) {
                const input = this.$refs._in;
                input.selectionStart = start;
                input.selectionEnd = end;
            },

            change() {
                /**
                 * Emitted when focus shifts away from this field after a change in value.
                 * @event change
                 * @type {string}
                 */
                this.$emit('change', this.toValue());
            },

            input() {
                /**
                 * Emitted at any change in value due to user input.
                 * @event input
                 * @type {string}
                 */
                this._sync();
                this.empty = !this.$refs._in.value;
                this.validate();
                this._emit(this.toValue());
            },

            keydown(e) {
                this._selectkey();
                this.$emit('keydown', e);
            },

            onEnter(e) {
                this.$emit('enter', e);
            },

            /** @private */
            _sync() {
                if (this.dirty) {
                    const value = this.ivalue;
                    const input = this.$refs._in;
                    if (!Object.is(value, this.toValue())) {
                        const ival = this.format(value);
                        input.value = ival;
                        this.empty = ival === '' || ival == null;
                        this.validate();
                    }
                    if (this.privacy && !this.focused) {
                        this.security = input.value.replace(/./g, bullet);
                    }
                    this.dirty = false;
                }
            },

            /** @private */
            _selectall(e) {
                const input = this.$refs._in;
                if (input.value.length) {
                    let selectEnd = 0;
                    if ($device.hasKeyboard) {
                        selectEnd = 2E9;
                        if (e.relatedTarget == null) {
                            this.dselect = true;
                        }
                    } else {
                        if (this.mselect) {
                            this.mselect.remove();
                        }
                        // autoselect placeholder for mobile; avoids unwanted caret and tooltips
                        const span = document.createElement('span');
                        span.classList.add('_fldl', '_flms');
                        span.textContent = input.value;
                        input.parentNode.insertBefore(span, input);
                        this.mselect = span;
                    }
                    setTimeout(() => {
                        this.select(0, selectEnd);
                    });
                }
            },

            /** @private */
            _selectoff(key) {
                this.dselect = false;
                if (this.mselect) {
                    this.mselect.remove();
                    this.mselect = null;
                }
            },

            /** @private */
            _selectkey() {
                if (this.mselect) {
                    this.$refs._in.value = '';
                    this._selectoff(true);
                }
            },

            /** @private */
            _focus(e) {
                if (this._pfocus) {
                    this._pfocus();
                }
                if (this.autoselect) {
                    this._selectall(e);
                }
                if (this.privacy) {
                    this.security = null;
                }
            },

            /** @private */
            _blur() {
                this._selectoff();
                if (this._pblur) {
                    this._pblur();
                }
                if (this.privacy) {
                    this.security = this.$refs._in.value.replace(/./g, bullet);
                }
            },

            /** @private */
            _mousedown(e) {
                if (this.mselect) {
                    this._selectoff();
                }
            },

            /** @private */
            _mouseup(e) {
                if (this.dselect) {
                    this.dselect = false;
                    e.preventDefault();
                }
            },

            /** @private */
            drop(e) {
                this.$emit('drop', e);
            },

            /** @private */
            paste(e) {
                this.$emit('paste', e);
            },

            /** @private */
            keypress(e) {
                this.$emit('keypress', e);
            },

            /** @private */
            _select() {
                if (this.inert) {
                    this.select(0, 0);
                }
            },

            /** @private */
            _warn() {
                if (this.warnto) {
                    this._warnend();
                    void this.$el.offsetWidth;
                }
                if (navigator.vibrate) {
                    navigator.vibrate(100);
                }
                this.$el.classList.add('_warn');
                this.$el.addEventListener('animationend', this._warnend);
            },

            /** @private */
            _warnend() {
                this.$el.classList.remove('_warn');
                this.$el.removeEventListener('animationend', this._warnend);
            },

            /**
             * Convert edited/displayed value to `this.value`.
             */
            toValue() {
                let value = this.$refs._in.value.trim();
                if (this.uppercase) {
                    value = value.toUpperCase();
                }
                if (this.lowercase) {
                    value = value.toLowerCase();
                }
                return value;
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    /** Outer wrapper */
    ._field,
    ._pick {
        position: relative;
        .font-size(@fs6);
        font-weight: 400;
        margin: 2px 3px;
        color: @fg;
        background-color: @bg-field;
        outline: none;
        width: 24ch;
        cursor: text;

        &               { .field-default(); }
        &._ebox         { .field-box(); }
        &._erounded     { .field-rounded(); }
        &._epill        { .field-pill(); }
        &._eframeless   { .field-frameless(); }
        &._eunderline   { .field-underline(); }
    }

    ._field {
        display: inline-flex;
        align-items: baseline;

        &    { .field-padding(4px);  }
        &.xs { .field-padding(0);    }
        &.sm { .field-padding(2px);  }
        &.lg { .field-padding(8px);  }
        &.xl { .field-padding(16px); }

        /** Multiline */
        &._ml {
            height: 4em;
            ._fldi,
            ._fldl,
            ._flds,
            ._flde {
                align-items: flex-start;
            }
            ._fldin {
                height: 100%;
            }
            &    ._fldin { .field-margin(-4px);  .field-padding(4px);  }
            &.xs ._fldin { .field-margin(0);     .field-padding(0);    }
            &.sm ._fldin { .field-margin(-2px);  .field-padding(2px);  }
            &.lg ._fldin { .field-margin(-8px);  .field-padding(8px);  }
            &.xl ._fldin { .field-margin(-16px); .field-padding(16px); }
        }

        /** Pill field - rounded ends */
        &.pill {
            border-radius: 999px;
        }

        &._warn {
            animation: _field-warn .4s linear;
        }

        /** Privacy mode */
        &._sec {
            ._fldin {
                opacity: 0;
            }
            ._fldl {
                opacity: 1;
                text-overflow: clip;
                & when not (@privacy-spacing = 0) {
                    letter-spacing: @privacy-spacing;
                }
            }
            &.numeric ._fldl {
                & when not (@privacy-spacing-num = 0) {
                    letter-spacing: @privacy-spacing-num;
                    -webkit-padding-start: @privacy-spacing-num / 2;
                    padding-inline-start: @privacy-spacing-num / 2;
                }
            }
        }

        ._tag {
            margin: 1px 0;
        }
    }

    ._fldi,
    ._flds,
    ._flde {
        display: inline-flex;
        align-items: center;
    }

    /** Inner wrapper */
    ._fldi {
        flex: 1 1 auto;
        position: relative;
        white-space: pre;
        min-width: 0;
        max-width: 100%;
        height: 100%;
    }

    /** Slot wrapper */
    ._flds {
        flex: 0 0 auto;
        align-self: stretch;
        -webkit-margin-start: -@field-inner-padding;
        -webkit-margin-end: calc(~'.25em + 4px');
        margin-inline-end: -@field-inner-padding;
        margin-inline-end: calc(~'.25em + 4px');
        color: @border-field;
    }
    ._flde {
        flex: 0 0 auto;
        align-self: stretch;
        -webkit-margin-start: calc(~'.25em + 4px');
        -webkit-margin-end: -@field-inner-padding;
        margin-inline-start: calc(~'.25em + 4px');
        margin-inline-end: -@field-inner-padding;
        color: @border-field;
    }

    /** Label */
    ._fldl {
        position: absolute;
        max-width: 100%;
        opacity: .5;
        overflow: hidden;
        text-overflow: ellipsis;
        pointer-events: none;
        &._dock {
            visibility: hidden;
        }
    }

    /** Input element */
    ._fldin {
        flex: 1 1 auto;
        width: 100%;
        min-width: 0;
        max-width: 100%;
        margin: 0;
        padding: 0;
        outline: none;
        border: none;
        border-radius: 0;
        font: inherit;
        color: inherit;
        cursor: inherit;
        background-color: transparent;
        box-shadow: none;
        box-sizing: content-box;
        -webkit-appearance: none;
           -moz-appearance: none;
                appearance: none;

        &[disabled] {
            opacity: .5;
        }
        &[readonly] {
            cursor: default;
        }
        &[type=number],
        &[type=date] {
            -moz-appearance: textfield;
        }
        &::selection {
            background: fade(@bg-highlight, @selection-opacity);
        }
        &::-moz-selection {
            background: fade(@bg-highlight, @selection-opacity);
        }
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none !important;
            margin: 0;
        }
    }
    textarea._fldin {
        resize: none;
    }
    ._fldis {
        flex: 1 1 auto;
        justify-content: inherit;
        display: inherit;
        outline: none;
    }

    /** Mobile autoselect placeholder */
    ._flms {
        background: fade(@bg-highlight, @selection-opacity);
        opacity: 1;
        + ._fldin {
            transform: scaleY(0);
            opacity: 0;
        }
    }

    /** script = "latn" */
    ._field._rtl._slatn {
        ._fldin {
            direction: ltr;
            text-align: right;
        }
    }
    ._field._rtl._slatn._astart {
        ._fldin {
            text-align: right;
        }
    }
    ._field._rtl._slatn._aend {
        ._fldin {
            text-align: left;
        }
    }

    /** align = "left" */
    ._field._aleft {
        ._fldi {
            justify-content: left;
        }
        ._fldin {
            text-align: left;
        }
    }

    /** align = "right" */
    ._field._aright {
        ._fldi {
            justify-content: right;
        }
        ._fldin {
            text-align: right;
        }
    }

    /** align = "start" */
    ._field._astart {
        ._fldi {
            justify-content: start;
        }
        ._fldin {
            text-align: start;
        }
    }

    /** align = "center" */
    ._field._acenter {
        ._fldi {
            justify-content: center;
        }
        ._fldin {
            text-align: center;
        }
    }

    /** align = "end" */
    ._field._aend {
        ._fldi {
            justify-content: flex-end;
        }
        ._fldin {
            text-align: end;
        }
    }

    @keyframes _field-warn {
        0% {
            background-color: fade(@bg-error, 10%);
        } 20% {
            background-color: transparent;
        } 40% {
            background-color: fade(@bg-error, 10%);
        }
    }

    ::-webkit-search-decoration,
    ::-webkit-search-cancel-button,
    ::-webkit-search-results-button,
    ::-webkit-search-results-decoration {
        -webkit-appearance: none;
    }
    ::-webkit-datetime-edit-fields-wrapper,
    ::-webkit-datetime-edit-month-field,
    ::-webkit-datetime-edit-day-field,
    ::-webkit-datetime-edit-year-field {
        padding: 0;
    }
    ::-webkit-calendar-picker-indicator {
        margin: 0;
        padding: 0;
    }
    ::-webkit-clear-button {
        display: none;
    }
    input[type=date] {
        &::-webkit-inner-spin-button {
            height: 1em;
            margin: 0;
        }
    }
</style>

<docs>
    Examples:
    ```jsx
    new Vue({
        data() {
            return {
                val: new Array(9)
            }
        },
        template:
            `<ib-context>
                <p><ib-field v-model="val[0]" placeholder="Default"/> Value: {{val[0]}}</p>
                <p><ib-field v-model="val[1]" placeholder=":multiline" multiline height="4em"/> Value: {{val[1]}}</p>
                <p><ib-field v-model="val[2]" placeholder=":maxlength=8" :maxlength="8"/> Value: {{val[2]}}</p>
                <p><ib-field v-model="val[3]" placeholder=":uppercase" uppercase/> Value: {{val[3]}}</p>
                <p><ib-field v-model="val[4]" placeholder=":lowercase" lowercase/> Value: {{val[4]}}</p>
                <p><ib-field v-model="val[5]" placeholder=":autocorrect :autocomplete :autocapitalize" autocorrect autocomplete autocapitalize/> Value: {{val[5]}}</p>
                <p><ib-field v-model="val[6]" placeholder=":disabled" disabled/> Value: {{val[6]}}</p>
                <p><ib-field v-model="val[7]" placeholder=":inert" inert/> Value: {{val[7]}}</p>
                <p><ib-field v-model="val[8]" placeholder=".pill" class="pill"/> Value: {{val[8]}}</p>
            </ib-context>`
    })
    ```
</docs>
